"use client"

import { Button, Card, Col, Divider, message, Row, theme } from "antd"
import { Voucher } from "@/lib/redux/features/voucher/type"
import { FC } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendarAlt, faCopy, faHotel, faPlaneCircleCheck, faPlaneSlash, faTicketAlt } from "@fortawesome/free-solid-svg-icons"
import Text from "@/components/common/Typography/Text"
import { useTranslation } from "react-i18next"
import { getValidDayjs } from "@/utils/safeParse/getValidDayjs"
import { useTERouter } from "@/hooks/app/useTERouter"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import _ from "lodash"
import { toast } from "react-toastify"
import {
  getVoucherDisplayAmount,
  getVoucherMaxDiscountAmount,
} from "@/utils/voucher/getVoucherDisplayAmount"

type PromotionItemProps = {
  voucher: Voucher
}

const PromotionItem: FC<PromotionItemProps> = ({ voucher }) => {
  const token = theme.useToken().token
  const { t, i18n } = useTranslation()
  const { getFormatCurrency } = useTETranslation()
  const router = useTERouter()
  const amount = getVoucherDisplayAmount(voucher)
  const maxAmount = getVoucherMaxDiscountAmount(voucher)
  const isPercent = voucher.valueType === "PERCENT"

  return (
    <Card
      size='small'
      hoverable
      onClick={() => router.push(voucher.productType.includes("FLIGHT") ? "/ve-may-bay" : "/hotels")}
    >
      <Row gutter={[15, 15]}>
        <Col>
          <FontAwesomeIcon
            style={{ color: token.colorPrimaryText }}
            icon={(
              voucher.productType === 'FLIGHTQN' || voucher.productType === 'FLIGHTQT'
                ? faPlaneCircleCheck
                : voucher.productType === 'HOTELQN' || voucher.productType === 'HOTELQT'
                  ? faHotel
                  : faTicketAlt
            )}
          />
        </Col>
        <Col flex={1} style={{ minWidth: 0 }}>
          <Row>
            <Text strong style={{ wordBreak: "break-word", lineHeight: 1.45 }}>
              {t("common.discount_1")}{" "}
              {isPercent
                ? `${amount}%`
                : i18n.language === "vi"
                  ? `${amount / 1000}K`
                  : getFormatCurrency(amount)}{" "}
              {_.lowerFirst(voucher.displayName)}
              {isPercent && maxAmount > 0 &&
                ` ${_.lowerFirst(t("common.max"))} ${getFormatCurrency(maxAmount)}`}
            </Text>
          </Row>
        </Col>
      </Row>
      <Divider style={{ marginBlock: 10 }} dashed />
      <Row gutter={[10, 10]} wrap align="middle">
        <Col flex="auto" style={{ minWidth: 0 }}>
          <div
            style={{
              borderRadius: token.borderRadius,
              background: token.colorBgLayout,
              paddingInline: "1rem",
              paddingBlock: "0.3rem",
            }}
          >
            <Text type="secondary" style={{ wordBreak: "break-all" }}>
              <FontAwesomeIcon icon={faCopy} /> {voucher.voucherCode}
            </Text>
          </div>
        </Col>
        <Col flex="none">
          <Button
            style={{ backgroundColor: token.colorPrimaryBg, color: token.colorPrimaryText }}
            type='primary'
            shape='round'
            htmlType='button'
            onClick={(e) => {
              e.stopPropagation()
              navigator.clipboard.writeText(voucher.voucherCode)
                .then(() => {
                  toast.success("Copy thành công!")
                })
                .catch(err => {
                  toast.error("Copy thất bại!")
                });
            }}
          >
            Copy
          </Button>
        </Col>
      </Row>
      <Row style={{ marginTop: '0.5rem' }}>
        <Text strong type='secondary' style={{ fontSize: '1.4rem' }}>
          <FontAwesomeIcon icon={faCalendarAlt} /> {t("promotion.expires_on")}: {getValidDayjs(voucher.endDate).format("DD/MM/YYYY")}
        </Text>
      </Row>
    </Card>
  )
}

export default PromotionItem